import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, LocateFixed, Save } from "lucide-react";
import { useField } from "../FieldContext";
import { Mono, Field, SelectField, TextArea, Btn } from "../ui";
import { getPosition } from "../lib/geo";

const BLANK = {
  name: "", site_number: "", asset_type: "Road - sealed", funding_measure: "EPAR",
  work_type: "", road_type: "", damage_type: "", where_on_asset: "",
  road_name: "", road_id: "", carriageway: "", chainage_start: "", chainage_end: "",
  latitude: null, longitude: null, notes: "",
};

export default function AssetForm() {
  const { claimId, assetId } = useParams();
  const editing = !!assetId;
  const { store, meta, version, bump, sync } = useField();
  const [form, setForm] = useState(BLANK);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (editing) {
      store.getRec("assets", assetId).then((a) => a && setForm({ ...BLANK, ...a }));
    }
  }, [editing, assetId, store, version]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const useLocation = async () => {
    setLocating(true);
    try {
      const pos = await getPosition();
      setForm((f) => ({ ...f, latitude: pos.latitude, longitude: pos.longitude }));
    } catch (e) {
      alert("Could not get your location. Enable GPS and try again.");
    } finally {
      setLocating(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== "" && v != null) payload[k] = v;
    });
    payload.name = form.name;
    try {
      if (editing) {
        await store.editAsset(assetId, payload);
        bump();
        nav(`/field/asset/${assetId}`);
      } else {
        const rec = await store.queueAsset(claimId, payload);
        bump();
        sync();
        nav(`/field/asset/${rec.id}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const fm = (meta?.funding_measures || []).map((m) => ({ value: m.code, label: `${m.code} — ${m.name}` }));

  return (
    <form onSubmit={save} className="space-y-4" data-testid="field-asset-form">
      <div>
        <Mono className="text-gold-400 text-[0.6rem]">{editing ? "Edit asset" : "New asset"}</Mono>
        <h1 className="font-heading text-2xl mt-1">Damaged asset</h1>
        <p className="text-white/45 text-xs mt-1 font-light">Identify and classify the asset. Costs are handled in The MDAu Office — not here.</p>
      </div>

      <Field label="Asset name / description" value={form.name} onChange={set("name")} testid="asset-name" required />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Site number" value={form.site_number} onChange={set("site_number")} testid="asset-site" />
        <SelectField label="Asset type" value={form.asset_type} onChange={set("asset_type")} options={meta?.asset_types || []} testid="asset-type" />
      </div>
      <SelectField label="Funding measure" value={form.funding_measure} onChange={set("funding_measure")} options={fm} testid="asset-funding" />
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Work type" value={form.work_type} onChange={set("work_type")} options={meta?.work_types || []} testid="asset-worktype" />
        <SelectField label="Road type" value={form.road_type} onChange={set("road_type")} options={meta?.road_types || []} testid="asset-roadtype" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Damage type" value={form.damage_type} onChange={set("damage_type")} options={meta?.damage_types || []} testid="asset-damagetype" />
        <SelectField label="Where on asset" value={form.where_on_asset} onChange={set("where_on_asset")} options={meta?.where_on_asset || []} testid="asset-where" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Road name" value={form.road_name} onChange={set("road_name")} testid="asset-roadname" />
        <Field label="Road ID" value={form.road_id} onChange={set("road_id")} testid="asset-roadid" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Carriageway" value={form.carriageway} onChange={set("carriageway")} testid="asset-carriageway" />
        <Field label="Chainage start" value={form.chainage_start} onChange={set("chainage_start")} testid="asset-chstart" />
        <Field label="Chainage end" value={form.chainage_end} onChange={set("chainage_end")} testid="asset-chend" />
      </div>

      <div className="border border-gold-500/20 bg-navy-900/40 rounded-sm p-3.5 space-y-2">
        <div className="flex items-center justify-between">
          <Mono className="text-gold-400/80 text-[0.55rem]">GPS location</Mono>
          <button type="button" onClick={useLocation} disabled={locating} data-testid="asset-use-location" className="inline-flex items-center gap-1.5 text-gold-300 text-xs">
            {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />} Use my location
          </button>
        </div>
        <div className="text-xs text-white/60" data-testid="asset-gps-value">
          {form.latitude != null ? `${form.latitude}, ${form.longitude}` : "No location captured yet"}
        </div>
      </div>

      <TextArea label="Notes" value={form.notes} onChange={set("notes")} testid="asset-notes" />

      <Btn type="submit" disabled={saving || !form.name} testid="asset-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {editing ? "Save changes" : "Create asset"}
      </Btn>
    </form>
  );
}
